import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import BusinessIcon from '@mui/icons-material/Business';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Box, IconButton, Stack, Tooltip } from '@mui/material';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import FundOpportunity from '../../models/FundOpportunity';
import { currentInvestment } from '../../models/Investor';
import ExtendedLiquid from '../../services/ExtendedLiquid';
import { StyledAvatar, StyledCard, StyledTypography } from '../styles/styles';
import { Palette } from 'Palette';

// Extended interface for fund opportunities with investor data
interface FundOpportunityWithInvestor extends FundOpportunity {
  investor?: any; // Using any to avoid import issues
}

interface FundOpportunityCardProps {
  opportunity: FundOpportunityWithInvestor;
  tone: keyof typeof Palette;
  onPromote?: () => void;
  onDemote?: () => void;
  onDelete?: () => void;
  compact?: boolean; // For recently viewed sidebar
}

const FundOpportunityCard: React.FC<FundOpportunityCardProps> = ({
  opportunity,
  tone,
  onPromote,
  onDemote,
  onDelete,
  compact = false,
}) => {
  const navigate = useNavigate();

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      NEW: '#607D8B',
      APPROVED: '#4CAF50',
      EOI: '#2196F3',
      APPLICATION_OPEN: '#FF9800',
      UNDER_MANAGEMENT: '#9C27B0',
      COMPLETE: '#455A64',
    };
    return colors[status] || '#607D8B';
  };

  return (
    <Box
      sx={{
        position: 'relative',
        cursor: 'pointer',
        width: '100%',
        boxSizing: 'border-box',
        '&:hover .action-overlay': {
          opacity: 1,
          visibility: 'visible',
        },
      }}
    >
      <StyledCard
        tone={tone}
        className="react-ContactCard"
        sx={{
          padding: '0.5rem 0.75rem !important', // Increased padding: 8px 12px
          marginBottom: '3px',
          width: '100%',
          maxWidth: '100%',
          minWidth: '0', // Allow shrinking
          overflow: 'hidden',
          minHeight: '2.8125rem', // 45px for overlay icons
          boxSizing: 'border-box', // Include padding in width calculation
          '& .MuiCardContent-root': {
            width: '100%',
            maxWidth: '100%',
            minWidth: '0',
            overflow: 'hidden',
            padding: '0 !important', // Remove default padding
          },
        }}
        onClick={() => navigate(`/opportunities/${opportunity.id}`)}
      >
        <Stack spacing={0.125} sx={{ width: '100%' }}>
          {/* Name row with small avatar */}
          <Stack direction="row" spacing={0.25} alignItems="center">
            <StyledAvatar
              tone={tone}
              sx={{
                width: '12px',
                height: '12px',
                fontSize: '0.4rem',
                backgroundColor: getStatusColor(opportunity.status),
              }}
            >
              <BusinessIcon sx={{ fontSize: '0.4rem' }} />
            </StyledAvatar>
            <StyledTypography
              fontWeight={700}
              tone={tone}
              sx={{
                fontSize: '0.6rem',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                flex: 1,
              }}
            >
              {opportunity.name || 'Unnamed Opportunity'}
            </StyledTypography>
          </Stack>

          {/* Status and financial info - full width */}
          <StyledTypography
            variant="body2"
            tone={tone}
            sx={{
              fontSize: '0.55rem',
              opacity: 0.8,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {opportunity.investor ? opportunity.investor.status : opportunity.status}
          </StyledTypography>

          {opportunity.investor &&
          opportunity.investor.status !== 'CANDIDATE' &&
          opportunity.investor.indicativeInvestment ? (
            <StyledTypography
              variant="body2"
              tone={tone}
              sx={{
                fontSize: '0.45rem',
                opacity: 0.7,
                fontStyle: 'italic',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              Investment:{' '}
              {ExtendedLiquid.currencyMillions(currentInvestment(opportunity.investor), true)}
            </StyledTypography>
          ) : opportunity.fundSize ? (
            <StyledTypography
              variant="body2"
              tone={tone}
              sx={{
                fontSize: '0.45rem',
                opacity: 0.7,
                fontStyle: 'italic',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              Fund Size: {ExtendedLiquid.currencyMillions(opportunity.fundSize, true)}
            </StyledTypography>
          ) : null}
        </Stack>
      </StyledCard>

      {/* Hover Overlay - show when not compact (always show View, optionally promote/demote/delete) */}
      {!compact && (
        <Box
          className="action-overlay"
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.85)',
            borderRadius: '8px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            opacity: 0,
            visibility: 'hidden',
            transition: 'opacity 0.2s ease',
            zIndex: 10,
            width: '100%',
            height: '100%',
            margin: 0,
            padding: 0,
          }}
        >
          <StyledTypography
            sx={{
              color: 'white',
              fontSize: '0.6rem',
              fontWeight: 700,
              marginBottom: '6px',
              textAlign: 'center',
              lineHeight: 1.1,
            }}
          >
            {opportunity.name || 'Unnamed Opportunity'}
          </StyledTypography>
          <Stack direction="row" spacing={0.5}>
            <Tooltip title="View Fund Opportunity">
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/opportunities/${opportunity.id}`);
                }}
                sx={{
                  width: '20px',
                  height: '20px',
                  backgroundColor: 'rgba(255,255,255,0.9)',
                  color: 'rgba(0,0,0,0.8)',
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,1)',
                    color: 'rgba(0,0,0,1)',
                  },
                  '& .MuiSvgIcon-root': {
                    fontSize: '0.7rem',
                  },
                }}
              >
                <VisibilityIcon />
              </IconButton>
            </Tooltip>
            {onPromote && (
              <Tooltip title="Promote">
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    onPromote();
                  }}
                  sx={{
                    width: '20px',
                    height: '20px',
                    backgroundColor: 'rgba(255,255,255,0.9)',
                    color: 'rgba(0,0,0,0.8)',
                    '&:hover': {
                      backgroundColor: 'rgba(255,255,255,1)',
                      color: 'rgba(0,0,0,1)',
                    },
                    '& .MuiSvgIcon-root': {
                      fontSize: '0.7rem',
                    },
                  }}
                >
                  <ArrowForwardIcon />
                </IconButton>
              </Tooltip>
            )}
            {onDemote && (
              <Tooltip title="Demote">
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    onDemote();
                  }}
                  sx={{
                    width: '20px',
                    height: '20px',
                    backgroundColor: 'rgba(255,255,255,0.9)',
                    color: 'rgba(0,0,0,0.8)',
                    '&:hover': {
                      backgroundColor: 'rgba(255,255,255,1)',
                      color: 'rgba(0,0,0,1)',
                    },
                    '& .MuiSvgIcon-root': {
                      fontSize: '0.7rem',
                    },
                  }}
                >
                  <ArrowBackIcon />
                </IconButton>
              </Tooltip>
            )}
            {onDelete && (
              <Tooltip title="Delete">
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete();
                  }}
                  sx={{
                    width: '20px',
                    height: '20px',
                    backgroundColor: 'rgba(255,255,255,0.9)',
                    color: 'rgba(0,0,0,0.8)',
                    '&:hover': {
                      backgroundColor: 'rgba(255,255,255,1)',
                      color: 'rgba(0,0,0,1)',
                    },
                    '& .MuiSvgIcon-root': {
                      fontSize: '0.7rem',
                    },
                  }}
                >
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Box>
      )}
    </Box>
  );
};

export default FundOpportunityCard;
